import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Hidden,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import uuid from 'react-uuid';

import { DEFAULT_WELCOME_MESSAGE, DEFAULT_UNAVAILABLE_MESSAGE } from 'constants/data';
import { useAppDispatch, useAppSelector } from 'store';
import {
  dismiss,
  selectPatient,
  clearPatientStore,
  selectPatientIsDismissed,
  setPatient as setPatientInStore,
} from 'store/patient';
import { ApiWaitingRoomLinkProfile, Patient } from 'types';
import { CircleCrossIcon, EmailIcon, HeavyCheckIcon, HospitalIcon } from 'assets';
import { useAzureAsset, useChat, useDialog, useLocalStorageState, useSignalrConnection, useVideo } from 'hooks';
import { MediaContainer, SettingsDialog, VideoPreviewWithControls, InfoDialog, TwoOptionsDialog } from 'components';
import { isPermissionDenied, noop, replaceURLsInString, stringifyIdentity } from 'utils';
import { WelcomeDialog } from '../WelcomeDialog';
import { CheckedInDialog } from '../CheckedInDialog';
import { EndCallDialog } from '../EndCallDialog';
import {
  updateIsProviderInQueueStatusInWaitingRoom,
  updateUnavailableProviderStatusInWaitingRoom,
} from 'store/waitingRoom';

import styles from './PatientWaitingRoom.module.scss';

type PatientConnectionStage = 'none' | 'patientDataAdded' | 'settingsCofigured' | 'checkFormClosed';

type PatientWaitingRoomProps = {
  profile: ApiWaitingRoomLinkProfile;
};

export function PatientWaitingRoom({ profile }: PatientWaitingRoomProps) {
  const dispatch = useAppDispatch();
  const patientInStore = useAppSelector(selectPatient);
  const isDismissed = useAppSelector(selectPatientIsDismissed);
  const navigate = useNavigate();
  const {
    updateCallData,
    events,
    signalrConnectionId,
    getToken,
    getIsProviderConnectedToQueue,
    disconnect,
    addEventListener,
    removeEventListener,
  } = useSignalrConnection();

  const {
    connect: videoConnect,
    isAcquiringLocalTracks,
    room,
    removeLocalVideoTrack,
    removeLocalAudioTrack,
  } = useVideo();
  const { showDialog } = useDialog();
  const avatarUrl = useAzureAsset(profile.linkToProfilePicture);
  const { identity, conversation, openChat } = useChat();
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md') && theme.breakpoints.up('sm'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [previousPatientDataToReconnect, setPreviousPatientDataToReconnect] = useLocalStorageState<Patient | null>(
    'previousPatientDataToReconnect',
  );
  const [patient, setPatient] = useState<Patient | null>(null);
  const [patientConnectionStage, setPatientConnectionStage] = useState<PatientConnectionStage>('none');
  const doNotHandleDisconnectionFromSignalr: MutableRefObject<boolean> = useRef<boolean>(false);
  const localTracksStartedAcquiring: MutableRefObject<boolean> = useRef<boolean>(false);

  const profileFullName = useMemo(() => {
    let fullName = profile.preferedDisplayName;
    if (!fullName) {
      fullName = [profile.title, profile.firstName, profile.lastName].filter((t) => t && t).join(' ');

      if (profile.suffix) {
        fullName += `, ${profile.suffix}`;
      }
    }

    return fullName;
  }, [profile]);

  const unavailableMessage = useMemo(() => {
    //eslint-disable-next-line
    return Boolean(profile.unavailableMessage) ? profile.unavailableMessage : DEFAULT_UNAVAILABLE_MESSAGE;
  }, [profile]);

  const welcomeMessage = useMemo(() => {
    //eslint-disable-next-line
    return Boolean(profile.welcomeMessage) ? profile.welcomeMessage : DEFAULT_WELCOME_MESSAGE;
  }, [profile]);

  function showTransferOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'transfer-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.zIndex = '9999';
    overlay.style.background = 'rgba(255, 255, 255, 0.95)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';

    const spinner = document.createElement('div');
    spinner.style.border = '6px solid #eee';
    spinner.style.borderTop = '6px solid #81CFDD';
    spinner.style.borderRadius = '50%';
    spinner.style.width = '48px';
    spinner.style.height = '48px';
    spinner.style.animation = 'spin 1s linear infinite';
    spinner.style.marginBottom = '2rem';

    const message = document.createElement('p');
    message.textContent = 'Transferring back to the waiting room. Please do not close this window.';
    message.style.fontSize = '1.6rem';
    message.style.color = '#444';
    message.style.textAlign = 'center';
    message.style.fontWeight = '500';
    message.style.maxWidth = '90%';

    overlay.appendChild(spinner);
    overlay.appendChild(message);
    document.body.appendChild(overlay);
  }

  ////////////////////////////////////////////////
  /// Events listening
  ////////////////////////////////////////////////

  const onKicked = useCallback(() => {
    doNotHandleDisconnectionFromSignalr.current = true;
    dispatch(dismiss());
    dispatch(clearPatientStore());
    disconnect();
    removeLocalAudioTrack();
    removeLocalVideoTrack();

    room?.disconnect();
    const modal = showDialog(InfoDialog, {
      title: 'You have been removed from the call. \n' + 'If you feel this is in error, please contact your provider. ',
      onConfirm: () => {
        modal.destroy();
        navigate('/login');
      },
    });
  }, [room, removeLocalAudioTrack, removeLocalVideoTrack]);

  const onCallEnded = useCallback(() => {
    console.log('📡 Patient received callEnded event');
    doNotHandleDisconnectionFromSignalr.current = true;
    dispatch(dismiss());
    dispatch(clearPatientStore());

    // ✅ Immediately clean up media and connection
    disconnect();

    if (room && room.localParticipant) {
      room.localParticipant.audioTracks.forEach(({ track }) => {
        track.stop();
        room.localParticipant.unpublishTrack(track);
      });

      room.localParticipant.videoTracks.forEach(({ track }) => {
        track.stop();
        room.localParticipant.unpublishTrack(track);
      });
    }

    removeLocalAudioTrack();
    removeLocalVideoTrack();
    room?.disconnect();

    // ✅ Now show dialog AFTER cleanup
    const modal = showDialog(InfoDialog, {
      title: 'The call has ended. You may now safely close your browser window.',
      onConfirm: () => {
        modal.destroy();
        console.log('🔁 Patient acknowledged call end dialog.');
        navigate('/login'); // ✅ No longer responsible for triggering cleanup
      },
    });
  }, [room, removeLocalAudioTrack, removeLocalVideoTrack, navigate]);

  const onDisconnected = useCallback(() => {
    if (doNotHandleDisconnectionFromSignalr.current) {
      return;
    }

    dispatch(dismiss());
    dispatch(clearPatientStore());
    disconnect();

    removeLocalAudioTrack();
    removeLocalVideoTrack();

    room?.disconnect();

    const modal = showDialog(InfoDialog, {
      title: 'You have been removed from the waiting room. If you feel this is in error, please contact your provider.',
      onConfirm: () => {
        modal.destroy();
        navigate('/login');
      },
    });
  }, [room, patient, removeLocalAudioTrack, removeLocalVideoTrack]);

  const onReconnected = useCallback(() => {
    if (doNotHandleDisconnectionFromSignalr.current) {
      return;
    }

    dispatch(dismiss());
    dispatch(clearPatientStore());

    removeLocalAudioTrack();
    removeLocalVideoTrack();

    room?.disconnect();

    const modal = showDialog(TwoOptionsDialog, {
      title: 'Disconnected',
      text: 'You have been disconnected. Probably because of bad internet connection.',
      confirmText: 'Reconnect',
      onConfirm: () => {
        setPreviousPatientDataToReconnect({ ...patient!, id: uuid() });
        modal.destroy();
        window.location.reload();
      },
    });
  }, [room, patient, removeLocalAudioTrack, removeLocalVideoTrack]);

  useEffect(() => {
    if (!signalrConnectionId) {
      console.warn('⏳ Waiting for SignalR connection...');
      return;
    }

    let retryAttempts = 0; // ✅ Retry counter
    const maxRetries = 5; // ✅ Max retry attempts
    const retryInterval = 500; // ✅ Retry every 500ms

    const checkSignalRConnection = () => {
      if (!window.signalrConnection || !window.signalrConnection.current) {
        retryAttempts++;

        if (retryAttempts > maxRetries) {
          console.error(`❌ SignalR connection failed after ${maxRetries} attempts. Giving up.`);
          return;
        }

        console.warn(
          `⚠️ SignalR connection not initialized. Retrying in ${retryInterval}ms... (Attempt ${retryAttempts}/${maxRetries})`,
        );

        setTimeout(checkSignalRConnection, retryInterval);
        return;
      }

      console.log('✅ SignalR connection established.');

      // ✅ Always bind "Transferred" listener immediately when connected
      window.signalrConnection.current.off('Transferred', handleTransferred);
      window.signalrConnection.current.on('Transferred', handleTransferred);

      // ✅ Continue with remaining logic only after check-in stage
      if (patientConnectionStage !== 'checkFormClosed') {
        console.warn('⏳ Patient has not completed check-in. Waiting to proceed with SignalR-dependent logic...');
        return;
      }

      // ✅ Any extra setup that needs patientConnectionStage === 'checkFormClosed' would go here
    };

    checkSignalRConnection();

    return () => {
      console.log('❌ Removing SignalR listener for "Transferred" event...');
      if (window.signalrConnection?.current) {
        window.signalrConnection.current.off('Transferred', handleTransferred);
      }
    };
  }, [
    signalrConnectionId,
    patientConnectionStage,
    dispatch,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    room,
    disconnect,
  ]);

  const handleTransferred = (newRoomUrlOrEvent?: string | CustomEvent) => {
    console.log('📡 Transferred event fired BEFORE callEnded');
    let newRoomUrl: string | undefined;

    if (typeof newRoomUrlOrEvent === 'string') {
      newRoomUrl = newRoomUrlOrEvent;
    } else if (newRoomUrlOrEvent?.detail) {
      newRoomUrl = newRoomUrlOrEvent.detail;
    }

    if (!newRoomUrl) {
      console.warn('⚠️ Transfer event triggered, but no valid newRoomUrl provided!');
      return;
    }

    console.log('✅ Transfer event received. Redirecting to:', newRoomUrl);

    const runTransfer = () => {
      if (patient) {
        console.log('💾 Storing transferred patient data for auto-check-in...');
        sessionStorage.setItem('transferredPatient', JSON.stringify(patient));
        sessionStorage.setItem('wasTransferred', 'true');
      } else {
        console.warn('⚠️ No patient data found before transfer. Auto-check-in might not work.');
      }

      showTransferOverlay();

      doNotHandleDisconnectionFromSignalr.current = true;

      console.log('🛑 Dismissing patient...');
      dispatch(dismiss());

      console.log('🗑️ Clearing patient store...');
      dispatch(clearPatientStore());

      console.log('🔌 Disconnecting from SignalR...');
      disconnect();

      console.log('🎤 Stopping local audio/video tracks...');
      removeLocalAudioTrack();
      removeLocalVideoTrack();

      console.log('📞 Disconnecting from room...');
      room?.disconnect();

      setTimeout(() => {
        console.log('🚀 Navigating to new room:', newRoomUrl);
        window.location.replace(newRoomUrl as string);
      }, 500);
    };

    // ✅ Check if patient has finished checking in
    if (patientConnectionStage !== 'checkFormClosed') {
      console.log('⏳ CheckedInDialog is still open, deferring transfer until it closes...');

      // 🔍 Try to detect if the dialog is already open
      const existingDialog = document.querySelector('[class*="MuiDialog-root"]');

      if (existingDialog) {
        console.log('🧼 Forcing CheckedInDialog to close...');
        existingDialog.parentNode?.removeChild(existingDialog);
        setPatientConnectionStage('checkFormClosed');
        setTimeout(runTransfer, 100); // let state update settle
      } else {
        // fallback: show and auto-confirm the dialog
        const modal = showDialog(CheckedInDialog, {
          onConfirm: () => {
            modal.destroy();
            setPatientConnectionStage('checkFormClosed');
            setTimeout(runTransfer, 100);
          },
        });
      }
    } else {
      runTransfer();
    }
  };

  useEffect(() => {
    console.log('📡 Registering additional SignalR listeners...');
    addEventListener('removedFromCall', onKicked);
    addEventListener('callEnded', onCallEnded);
    addEventListener('disconnected', onDisconnected);
    addEventListener('reconnected', onReconnected);

    return () => {
      setTimeout(() => {
        console.log('❌ Delayed removal of SignalR listeners...');
        removeEventListener('removedFromCall', onKicked);
        removeEventListener('callEnded', onCallEnded);
        removeEventListener('disconnected', onDisconnected);
        removeEventListener('reconnected', onReconnected);
      }, 1500); // ✅ Delay removal to avoid premature cleanup
    };
  }, [onKicked, onCallEnded, onDisconnected, onReconnected]);

  const onStartCall = (roomId: string, id: string) => {
    if (patientInStore && id) {
      const identity = stringifyIdentity([id, patientInStore.userName]);

      getToken({ identity, roomId })
        .then((token) => {
          videoConnect(token);
        })
        .catch(noop);
    }
  };

  useEffect(() => {
    events.on('joinCall', onStartCall);
    events.on('updatedUnavailableProviderStatus', (unavailable: boolean) =>
      dispatch(updateUnavailableProviderStatusInWaitingRoom(unavailable)),
    );
    events.on('isProviderInQueueSent', (isProviderInQueue: boolean) =>
      dispatch(updateIsProviderInQueueStatusInWaitingRoom(isProviderInQueue)),
    );
  }, [events]);

  ////////////////////////////////////////////////
  /// Preparing patinet to connection
  ////////////////////////////////////////////////

  const updatePatientData = async () => {
    const isCameraPermissionDenied = await isPermissionDenied('camera');
    const isMicrophonePermissionDenied = await isPermissionDenied('microphone');
    updateCallData({
      callAccesses: { hasMicroAccess: !isMicrophonePermissionDenied, hasCameraAccess: !isCameraPermissionDenied },
    });
  };

  useEffect(() => {
    if (signalrConnectionId && patientConnectionStage === 'settingsCofigured') {
      updatePatientData().catch(noop);
      getIsProviderConnectedToQueue();

      const modal = showDialog(CheckedInDialog, {
        onConfirm: () => {
          modal.destroy();
          setPatientConnectionStage('checkFormClosed');
        },
      });
    }
  }, [patient, signalrConnectionId, patientConnectionStage]);

  useEffect(() => {
    if (
      patientConnectionStage === 'patientDataAdded' &&
      !isAcquiringLocalTracks &&
      localTracksStartedAcquiring.current
    ) {
      const modal = showDialog(SettingsDialog, {
        onConfirm: async () => {
          dispatch(setPatientInStore(patient!));
          setPatientConnectionStage('settingsCofigured');

          modal.destroy();
        },
        onClose: () => {
          dispatch(setPatientInStore(patient!));
          setPatientConnectionStage('settingsCofigured');
        },
      });
    }

    if (!localTracksStartedAcquiring.current && isAcquiringLocalTracks) {
      localTracksStartedAcquiring.current = true;
    }
  }, [patientConnectionStage, isAcquiringLocalTracks]);

  useEffect(() => {
    const transferredSession = sessionStorage.getItem('transferredPatient');
    const wasTransferred = sessionStorage.getItem('wasTransferred'); // ✅ New flag

    if (wasTransferred && transferredSession) {
      console.log('🚀 Patient was transferred. Skipping WelcomeDialog and auto-checking in...');
      sessionStorage.removeItem('transferredPatient'); // ✅ Clear after processing
      sessionStorage.removeItem('wasTransferred'); // ✅ Remove transfer flag

      const transferredPatient = JSON.parse(transferredSession);
      setPatient(transferredPatient);
      dispatch(setPatientInStore(transferredPatient));
      setPatientConnectionStage('settingsCofigured'); // ✅ Skip WelcomeDialog
      return;
    }

    if (patientConnectionStage === 'none') {
      if (previousPatientDataToReconnect) {
        console.log('🔄 Reconnecting previous patient session...');
        setPatient(previousPatientDataToReconnect);
        dispatch(setPatientInStore(previousPatientDataToReconnect));
        setPreviousPatientDataToReconnect(null);
        setPatientConnectionStage('settingsCofigured');
        return;
      }

      console.log('📡 New patient session detected. Showing WelcomeDialog...');
      const modal = showDialog(WelcomeDialog, {
        avatarUrl,
        organizationName: profile.organization,
        profileFullName,
        onConfirm: (patient: Patient) => {
          setPatient(patient);
          setPatientConnectionStage('patientDataAdded');
          modal.destroy();
          setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          }, 300);
        },
      });
    }
  }, [patientConnectionStage, previousPatientDataToReconnect]);

  const handleMessageClick = (): void => {
    if (!conversation && !profile.unavailable && profile.isProviderInQueue) {
      openChat(profile.providerId, identity);
    }
  };

  return (
    <>
      {patient && !isDismissed && <MediaContainer />}

      <Hidden smUp>
        <Box>{!isDismissed && <VideoPreviewWithControls />}</Box>
      </Hidden>

      <Card
        className={classnames(styles.main, {
          [styles.mainTablet]: isTablet,
          [styles.mainMobile]: isMobile,
        })}
      >
        <CardHeader title={profile.organization} />
        <CardContent
          className={classnames(styles.content, {
            [styles.contentTablet]: isTablet,
            [styles.contentMobile]: isMobile,
          })}
        >
          <Stack direction='row' spacing={'20px'} alignItems='flex-start' justifyContent='space-between'>
            <Stack direction='column' spacing={'20px'}>
              {patient && !isDismissed && (
                <Typography component='p' variant='h2'>
                  {`Hi, ${patient.userName}`}
                </Typography>
              )}
              {!isDismissed &&
                profile.isProviderInQueue !== undefined &&
                (profile.unavailable || !profile.isProviderInQueue ? (
                  <Typography component='p' variant='body2'>
                    {unavailableMessage}
                  </Typography>
                ) : (
                  <Typography component='p' variant='body2'>
                    {welcomeMessage}
                  </Typography>
                ))}

              <Hidden smDown>
                <Stack direction='row' spacing={'20px'} alignItems='flex-start'>
                  <Stack spacing={'10px'} alignItems='flex-start'>
                    <Avatar
                      sx={{
                        maxWidth: 150,
                        maxHeight: 150,
                        minWidth: 100,
                        minHeight: 100,
                        width: '100%',
                        height: '100%',
                        flex: 1,
                        borderRadius: 3,
                      }}
                      variant='square'
                      src={avatarUrl}
                    >
                      {profile.firstName}
                    </Avatar>

                    {!isDismissed && (
                      <Button
                        variant='contained'
                        disabled={profile.unavailable || !profile.isProviderInQueue}
                        className={styles.messageBtn}
                        onClick={handleMessageClick}
                      >
                        Message
                      </Button>
                    )}
                  </Stack>

                  <Stack direction='column' spacing={'20px'}>
                    <Stack spacing={'7px'}>
                      <Typography component='p' variant='h4'>
                        {profileFullName}
                      </Typography>

                      <Typography component='p' variant='body2' color='#979797'>
                        <i>{profile.speciality}</i>
                      </Typography>
                    </Stack>

                    {!isDismissed &&
                      profile.isProviderInQueue !== undefined &&
                      (profile.unavailable || !profile.isProviderInQueue ? (
                        <Stack direction='row' spacing='10px' alignItems='center'>
                          <CircleCrossIcon sx={{ color: '#626363', fontSize: '1.6rem' }} />
                          <Typography component='p' variant='body2'>
                            Unavailable
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction='row' spacing='10px' alignItems='center'>
                          <HeavyCheckIcon />
                          <Typography component='p' variant='body2'>
                            Available
                          </Typography>
                        </Stack>
                      ))}

                    <Stack direction='row' spacing={'10px'} alignItems='center'>
                      <HospitalIcon className={styles.hospitalIcon} />
                      <Typography component='p' variant='body2'>
                        {profile.organization}
                      </Typography>
                    </Stack>

                    {profile.displayEmail && (
                      <Stack direction='row' spacing={'10px'} alignItems='center'>
                        <EmailIcon className={styles.emailIcon} color='primary' />
                        <Typography component='p' variant='body2'>
                          {profile.email}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Hidden>

              <Hidden smUp>
                <Stack direction='column' spacing={'15px'} alignItems='flex-start'>
                  <Stack direction='row' spacing={'15px'}>
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        flex: 1,
                      }}
                      variant='square'
                      src={avatarUrl}
                    >
                      {profile.firstName}
                    </Avatar>

                    <Stack spacing={'10px'} justifyContent='flex-end'>
                      {!isDismissed &&
                        profile.isProviderInQueue !== undefined &&
                        (profile.unavailable || !profile.isProviderInQueue ? (
                          <Stack direction='row' spacing='11px' alignItems='center'>
                            <CircleCrossIcon sx={{ color: '#626363', fontSize: '1.6rem' }} />
                            <Typography component='p' variant='body2'>
                              Unavailable
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack direction='row' spacing='11px' alignItems='center'>
                            <HeavyCheckIcon />
                            <Typography component='p' variant='body2'>
                              Available
                            </Typography>
                          </Stack>
                        ))}

                      {!isDismissed && (
                        <Button
                          variant='contained'
                          disabled={profile.unavailable || !profile.isProviderInQueue}
                          className={styles.messageBtn}
                          onClick={handleMessageClick}
                        >
                          Message
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Typography component='p' variant='h4'>
                    {profileFullName}
                  </Typography>

                  <Stack direction='row' spacing={'20px'} alignItems='center'>
                    <HospitalIcon className={styles.hospitalIcon} />
                    <Typography component='p' variant='body2'>
                      {profile.organization}
                    </Typography>
                  </Stack>

                  {profile.displayEmail && (
                    <Stack direction='row' spacing={'20px'} alignItems='center'>
                      <EmailIcon className={styles.emailIcon} color='primary' />
                      <Typography component='p' variant='body2'>
                        {profile.email}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Hidden>
            </Stack>
            <Hidden mdUp smDown>
              <Box className={styles.preview}>
                {!isDismissed && (
                  <VideoPreviewWithControls
                    previewClassName={styles.videoPreview}
                    controlsClassName={styles.videoControls}
                  />
                )}
              </Box>
            </Hidden>
          </Stack>

          <Stack spacing='15px' alignItems='flex-start'>
            <Typography component='pre' variant='body1' whiteSpace='pre-wrap' className={classnames(styles.bio)}>
              {replaceURLsInString(profile.biography || '')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
